import {
  useGetDashboardSummary,
  getGetDashboardSummaryQueryKey,
  useGetInboundVerificationStatsByMailbox,
  getGetInboundVerificationStatsByMailboxQueryKey,
  useGetPubsubMetrics,
  getGetPubsubMetricsQueryKey,
  type DashboardEngagement,
} from "@workspace/api-client-react";
import { INBOUND_VERIFICATION_FILTER_VALUES } from "@/lib/enumValues";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  XCircle,
  Clock,
  Send,
  FileCode2,
  TrendingUp,
  Users,
  PenSquare,
  Plus,
  Palette,
  ScrollText,
  RefreshCw,
  Eye,
  MousePointerClick,
  KeyRound,
} from "lucide-react";
import { LastUpdatedIndicator } from "@/components/LastUpdatedIndicator";
import { Link } from "wouter";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
} from "recharts";
import {
  ShieldCheck,
  ShieldX,
  ShieldQuestion,
  ShieldAlert,
} from "lucide-react";
import { SenderStatusIndicator } from "@/components/SenderStatusIndicator";
import { useLookbackDays, LOOKBACK_OPTIONS } from "@/hooks/useLookbackDays";

// Format a `YYYY-MM-DD` UTC date as a short localized label (e.g. "Apr
// 23"). Anchors at noon UTC so DST transitions don't push the label
// onto the previous local day.
function formatDayLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map((n) => Number(n));
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const DASHBOARD_REFETCH_INTERVAL_MS = 30_000;
// Grace period beyond the polling interval before we flag the "Updated …" label
// as stale. A late refetch (slow network, brief tab throttling) shouldn't
// flicker the indicator into a warning state.
const DASHBOARD_STALE_GRACE_MS = 15_000;

export default function Dashboard() {
  const { data, isLoading, error, dataUpdatedAt, isFetching, refetch } =
    useGetDashboardSummary({
      query: {
        queryKey: getGetDashboardSummaryQueryKey(),
        refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
      },
    });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center text-destructive">
        Failed to load dashboard data.
      </div>
    );
  }

  // Lifetime success rate: sent / (sent + failed). When the user has zero
  // attempted sends we render an em dash so we never display a misleading
  // "0% success" before they've sent anything.
  const totalAttempted = data.totalSent + data.totalFailed;
  const successRatePercent =
    totalAttempted > 0
      ? Math.round((data.totalSent / totalAttempted) * 100)
      : null;
  const successRateColor =
    successRatePercent === null
      ? "text-muted-foreground"
      : successRatePercent >= 95
        ? "text-emerald-600 dark:text-emerald-400"
        : successRatePercent >= 80
          ? "text-amber-600 dark:text-amber-400"
          : "text-destructive";

  const sender = data.senderHealth;

  // Daily volume max for the y-axis hint shown next to the chart header.
  const dailyMax = data.dailyVolume.reduce(
    (m, d) => Math.max(m, d.sent + d.failed),
    0,
  );

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-5xl mx-auto space-y-8">

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-2">Dashboard</h1>
            <p className="text-muted-foreground">Overview of your email operations.</p>
            <div className="mt-2 flex items-center gap-3">
              <LastUpdatedIndicator
                updatedAt={dataUpdatedAt}
                isFetching={isFetching}
                staleAfterMs={DASHBOARD_REFETCH_INTERVAL_MS + DASHBOARD_STALE_GRACE_MS}
                testId="dashboard-last-updated"
                staleSuffix=" — counts may be out of date"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
                aria-label="Refresh dashboard counts"
                aria-busy={isFetching}
                data-testid="dashboard-refresh-button"
              >
                {isFetching ? (
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1.5" />
                )}
                Refresh
              </Button>
            </div>
          </div>
          {/* Sender health pill — at-a-glance deliverability status */}
          <SenderStatusIndicator
            senderConfig={sender}
            testIdSuffix="-dashboard"
            showUnverified
            unverifiedHref="/app/brand"
            showRecheckButton
            recheckButtonTestId="sender-recheck-button-dashboard"
            showValidationWarning
          />
        </div>

        {/* Quick Actions — one-click access to the most-used flows */}
        <Card data-testid="dashboard-quick-actions">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Quick actions</CardTitle>
            <CardDescription>Jump straight into the flows you use the most.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Button
                asChild
                variant="default"
                className="justify-start"
                data-testid="quick-action-compose"
              >
                <Link href="/app/compose">
                  <PenSquare className="w-4 h-4 mr-2" />
                  Compose email
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="justify-start"
                data-testid="quick-action-new-template"
              >
                <Link href="/app/templates/new">
                  <Plus className="w-4 h-4 mr-2" />
                  New template
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="justify-start"
                data-testid="quick-action-brand"
              >
                <Link href="/app/brand">
                  <Palette className="w-4 h-4 mr-2" />
                  Brand settings
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="justify-start"
                data-testid="quick-action-logs"
              >
                <Link href="/app/logs">
                  <ScrollText className="w-4 h-4 mr-2" />
                  View logs
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid — 4 KPIs */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card data-testid="dashboard-stat-total-sent">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total sent</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.totalSent.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.sentLast7Days.toLocaleString()} in the last 7 days
              </p>
            </CardContent>
          </Card>
          <Card data-testid="dashboard-stat-success-rate">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${successRateColor}`}>
                {successRatePercent === null ? "—" : `${successRatePercent}%`}
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                {data.totalFailed > 0 ? (
                  <>
                    <XCircle className="w-3 h-3 text-destructive" />
                    {data.totalFailed.toLocaleString()} failed lifetime
                  </>
                ) : (
                  "No failures yet"
                )}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="dashboard-stat-unique-recipients">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unique recipients</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {data.uniqueRecipientsTotal.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.uniqueRecipientsLast7Days.toLocaleString()} in the last 7 days
              </p>
            </CardContent>
          </Card>
          <Card data-testid="dashboard-stat-templates">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Templates</CardTitle>
              <FileCode2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data.userTemplatesCount + data.builtinTemplatesCount}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.userTemplatesCount} yours, {data.builtinTemplatesCount} built-in
              </p>
            </CardContent>
          </Card>
        </div>

        {/* 14-day send activity chart */}
        <Card data-testid="dashboard-activity-chart">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base">Send activity (last 14 days)</CardTitle>
                <CardDescription>
                  Daily delivered vs failed sends, bucketed by UTC date.
                </CardDescription>
              </div>
              <div className="text-xs text-muted-foreground">
                Peak day: <span className="font-medium text-foreground">{dailyMax.toLocaleString()}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {dailyMax === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-10">
                No activity yet in the last 14 days.{" "}
                <Link href="/app/compose" className="text-primary hover:underline">
                  Send your first email
                </Link>
                .
              </div>
            ) : (
              <div className="h-56 w-full" data-testid="dashboard-activity-chart-svg">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.dailyVolume.map((d) => ({
                      day: formatDayLabel(d.date),
                      Sent: d.sent,
                      Failed: d.failed,
                    }))}
                    margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={32}
                    />
                    <RTooltip
                      cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "6px",
                        fontSize: "12px",
                      }}
                      labelStyle={{ color: "hsl(var(--foreground))" }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "12px" }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Bar dataKey="Sent" stackId="a" fill="hsl(221 83% 53%)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Failed" stackId="a" fill="hsl(0 84% 60%)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Engagement (last 14 days). Only render when the API has the
            `engagement` block — older clients hitting an upgraded API
            still work, and a fresh API hitting an offline DB returns
            zero-value cards rather than crashing. */}
        {data.engagement && (
          <EngagementSection engagement={data.engagement} />
        )}

        <PubSubMetricsSection />

        <MailboxVerificationSection />

        {/* Top Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top templates used</CardTitle>
          </CardHeader>
          <CardContent>
            {data.topTemplates.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">No templates used yet.</div>
            ) : (
              <div className="space-y-3">
                {data.topTemplates.map((t) => (
                  <div key={t.templateId} className="flex items-center justify-between text-sm">
                    <Link href={`/app/templates/${t.templateId}`} className="font-medium text-primary hover:underline truncate mr-4">
                      {t.templateName}
                    </Link>
                    <span className="text-muted-foreground whitespace-nowrap">{t.sentCount} sends</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>


        {/* Recent Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent activity</CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentLogs.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                No emails sent recently. <Link href="/app/compose" className="text-primary hover:underline">Send one now</Link>.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.recentLogs.map((log) => (
                  <div key={log.id} className="py-3 flex items-center justify-between">
                    <div className="min-w-0 flex-1 mr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${log.status === "sent" ? "bg-green-500" : "bg-red-500"}`} />
                        <Link href={`/app/logs/${log.id}`} className="font-medium text-sm text-foreground hover:text-primary truncate block">
                          {log.subject}
                        </Link>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground truncate">
                        <span>To: {log.to}</span>
                        <span>&bull;</span>
                        <span className="truncate font-mono">From: {log.fromAddress}</span>
                      </div>
                    </div>
                    <div className="flex items-center text-xs text-muted-foreground whitespace-nowrap">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(log.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {data.recentLogs.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border text-center">
                <Link href="/app/logs" className="text-sm text-primary hover:underline font-medium">
                  View all logs &rarr;
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}

/**
 * 14-day engagement panel: open / click rate cards plus a small
 * combined trend chart. We render this only when the API response
 * includes the `engagement` block, so this stays a pure presentational
 * component (no fetching of its own).
 *
 * Rates are pre-computed on the server as fractions in [0, 1] so we
 * just multiply by 100 here. The "of N sent" subtext quietly explains
 * what the denominator is so a 0% card with low send volume reads as
 * "you haven't sent enough yet" rather than "tracking is broken".
 */
function EngagementSection({
  engagement,
}: {
  engagement: DashboardEngagement;
}) {
  const formatPct = (rate: number) => `${(rate * 100).toFixed(1)}%`;
  const sentLabel = engagement.sentLast14Days === 1 ? "send" : "sends";
  const trendData = engagement.dailyEngagement.map((d) => ({
    day: formatDayLabel(d.date),
    Opens: d.opens,
    Clicks: d.clicks,
  }));
  const trendMax = engagement.dailyEngagement.reduce(
    (m, d) => Math.max(m, d.opens, d.clicks),
    0,
  );

  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="dashboard-engagement">
      <Card data-testid="dashboard-engagement-open-rate">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Open rate (14d)</CardTitle>
          <Eye className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {engagement.sentLast14Days === 0
              ? "—"
              : formatPct(engagement.openRate14Days)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {engagement.uniqueOpensLast14Days.toLocaleString()} unique
            {" "}of {engagement.sentLast14Days.toLocaleString()} {sentLabel}
          </p>
        </CardContent>
      </Card>
      <Card data-testid="dashboard-engagement-click-rate">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Click rate (14d)</CardTitle>
          <MousePointerClick className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {engagement.sentLast14Days === 0
              ? "—"
              : formatPct(engagement.clickRate14Days)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {engagement.uniqueClicksLast14Days.toLocaleString()} unique
            {" "}of {engagement.sentLast14Days.toLocaleString()} {sentLabel}
          </p>
        </CardContent>
      </Card>
      <Card data-testid="dashboard-engagement-trend">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Engagement trend</CardTitle>
          <CardDescription className="text-xs">
            Opens vs clicks per day, last 14 days. Bot events excluded.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {trendMax === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-6">
              No engagement events yet. Enable open/click tracking in{" "}
              <Link href="/app/brand" className="text-primary hover:underline">
                Brand Settings
              </Link>{" "}
              to start collecting data.
            </div>
          ) : (
            <div className="h-24 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trendData}
                  margin={{ top: 4, right: 4, bottom: 0, left: -28 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={28}
                  />
                  <RTooltip
                    cursor={{ stroke: "hsl(var(--muted))", opacity: 0.4 }}
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: "11px",
                    }}
                  />
                  <Line type="monotone" dataKey="Opens" stroke="hsl(221 83% 53%)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Clicks" stroke="hsl(142 71% 45%)" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const PUBSUB_METRICS_REFETCH_MS = 30_000;

function PubSubMetricsSection() {
  const { data, isLoading, error } = useGetPubsubMetrics({
    query: {
      queryKey: getGetPubsubMetricsQueryKey(),
      refetchInterval: PUBSUB_METRICS_REFETCH_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    },
  });

  if (isLoading) {
    return (
      <Card data-testid="pubsub-metrics-chart">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Pub/Sub auth methods</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card data-testid="pubsub-metrics-chart">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Pub/Sub auth methods</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive text-center py-6">
            Failed to load Pub/Sub metrics.
          </div>
        </CardContent>
      </Card>
    );
  }

  const totals = data.total;
  const grandTotal = (totals.jwt ?? 0) + (totals.shared_token ?? 0);
  const unflushed = data.process;
  const unflushedTotal = (unflushed.jwt ?? 0) + (unflushed.shared_token ?? 0);
  const flushed = data.historical;
  const flushedTotal = (flushed.jwt ?? 0) + (flushed.shared_token ?? 0);
  const daily = data.daily ?? [];
  const dailyMax = daily.reduce(
    (m, d) => Math.max(m, d.jwt + d.shared_token),
    0,
  );
  const trendData = daily.map((d) => ({
    day: formatDayLabel(d.date),
    JWT: d.jwt,
    "Shared Token": d.shared_token,
  }));

  return (
    <Card data-testid="pubsub-metrics-chart">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Pub/Sub auth methods (last 30 days)</CardTitle>
            </div>
            <CardDescription className="mt-1">
              Daily JWT vs shared-token authentication trend.
            </CardDescription>
          </div>
          <div className="text-xs text-muted-foreground">
            All-time total: <span className="font-medium text-foreground">{grandTotal.toLocaleString()}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div
          className="grid gap-3 sm:grid-cols-2 mb-4"
          data-testid="pubsub-metrics-persistence"
        >
          <div
            className="rounded-md border border-border p-3"
            data-testid="pubsub-metrics-unflushed"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Unflushed (Redis)
              </span>
              <span
                className="text-lg font-semibold tabular-nums"
                data-testid="pubsub-metrics-unflushed-total"
              >
                {unflushedTotal.toLocaleString()}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              JWT {(unflushed.jwt ?? 0).toLocaleString()} ·
              {" "}Shared {(unflushed.shared_token ?? 0).toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              In-memory counters (hydrated from Redis on restart), pending the
              next minute's flush to the database.
            </p>
          </div>
          <div
            className="rounded-md border border-border p-3"
            data-testid="pubsub-metrics-flushed"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Flushed (DB)
              </span>
              <span
                className="text-lg font-semibold tabular-nums"
                data-testid="pubsub-metrics-flushed-total"
              >
                {flushedTotal.toLocaleString()}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              JWT {(flushed.jwt ?? 0).toLocaleString()} ·
              {" "}Shared {(flushed.shared_token ?? 0).toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Durably persisted totals across all process restarts.
            </p>
          </div>
        </div>
        {grandTotal === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-10">
            No Pub/Sub authentication events recorded yet.
          </div>
        ) : dailyMax === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-10">
            No Pub/Sub events in the last 30 days. {grandTotal.toLocaleString()} event{grandTotal !== 1 ? "s" : ""} recorded before this window.
          </div>
        ) : (
          <div className="h-56 w-full" data-testid="pubsub-metrics-chart-svg">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={trendData}
                margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                barCategoryGap="20%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <RTooltip
                  cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "12px" }}
                  iconType="circle"
                  iconSize={8}
                />
                <Bar dataKey="JWT" stackId="a" fill="hsl(221 83% 53%)" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Shared Token" stackId="a" fill="hsl(32 95% 55%)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const VERIFICATION_COLORS: Record<string, string> = {
  bimi: "bg-emerald-500",
  dmarc: "bg-blue-500",
  unverified: "bg-gray-400",
  fail: "bg-destructive",
};

const VERIFICATION_LABELS: Record<string, { label: string; color: string; icon: typeof ShieldCheck }> = {
  bimi: { label: "BIMI", color: "text-emerald-600", icon: ShieldCheck },
  dmarc: { label: "DMARC", color: "text-blue-600", icon: ShieldCheck },
  unverified: { label: "Unverified", color: "text-muted-foreground", icon: ShieldQuestion },
  fail: { label: "Failed", color: "text-destructive", icon: ShieldX },
};

function MailboxVerificationSection() {
  const [lookbackDays, setLookbackDays] = useLookbackDays();
  const { data, isLoading } = useGetInboundVerificationStatsByMailbox(
    { days: lookbackDays },
    {
      query: {
        queryKey: getGetInboundVerificationStatsByMailboxQueryKey({ days: lookbackDays }),
        refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
      },
    },
  );

  if (isLoading || !data || data.mailboxes.length === 0) return null;

  return (
    <div data-testid="dashboard-mailbox-verification">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ShieldAlert className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-base font-semibold tracking-tight">
          Mailbox authentication (last {data.days}d)
        </h2>
        <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 w-fit ml-auto" data-testid="dashboard-lookback-selector">
          {LOOKBACK_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => setLookbackDays(d)}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                lookbackDays === d
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`dashboard-lookback-${d}d`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.mailboxes.map((mb) => {
          const failCount =
            (mb.breakdown.fail ?? 0) + (mb.breakdown.unverified ?? 0);
          const passCount =
            (mb.breakdown.bimi ?? 0) + (mb.breakdown.dmarc ?? 0);
          const failPct =
            mb.total > 0 ? Math.round((failCount / mb.total) * 100) : 0;

          return (
            <Link
              key={mb.mailboxId}
              href={`/app/inbox?mailbox=${encodeURIComponent(mb.address)}`}
              className="block"
            >
              <Card
                className="hover:ring-1 hover:ring-primary/30 transition-shadow cursor-pointer"
                data-testid={`mailbox-verification-tile-${mb.mailboxId}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium truncate">
                    {mb.displayName || mb.address}
                  </CardTitle>
                  <CardDescription className="text-xs truncate">
                    {mb.address}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                    {INBOUND_VERIFICATION_FILTER_VALUES.map(
                      (key) => {
                        const count = mb.breakdown[key] ?? 0;
                        if (count === 0) return null;
                        const pct = (count / mb.total) * 100;
                        return (
                          <div
                            key={key}
                            className={`${VERIFICATION_COLORS[key]} transition-all`}
                            style={{ width: `${pct}%` }}
                            title={`${VERIFICATION_LABELS[key].label}: ${count} (${Math.round(pct)}%)`}
                          />
                        );
                      },
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {INBOUND_VERIFICATION_FILTER_VALUES.map(
                      (key) => {
                        const info = VERIFICATION_LABELS[key];
                        const count = mb.breakdown[key] ?? 0;
                        const Icon = info.icon;
                        return (
                          <div
                            key={key}
                            className="flex items-center gap-1"
                          >
                            <Icon className={`w-3 h-3 ${info.color}`} />
                            <span className={info.color}>{info.label}</span>
                            <span className="ml-auto font-mono tabular-nums text-muted-foreground">
                              {count}
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {mb.total} msg{mb.total !== 1 ? "s" : ""} — {passCount} auth,{" "}
                    {failCount} unauth
                    {failPct > 0 && (
                      <span className="text-destructive ml-1">
                        ({failPct}%)
                      </span>
                    )}
                  </p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
